import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Badge } from "../../../components/ui/badge";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import { useApprovalRequests } from "../../../hooks/useApprovalRequests";
import { useAuth } from "../../../contexts/authContext";
import { formattedDate } from "../../../lib/utils";
import { ApprovalRequest } from "../../../models/approval";
import {
  formatEntityType,
  formatChangeType,
  formatStatus,
  formatSnapshotFieldName,
  formatSnapshotValue,
} from "../../../lib/approvalLabels";
import { getEntityLink } from "../../../lib/approvalLabels";
import { hasPermission } from "../../../lib/permissions";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { ControlChangeDiff } from "./components/ControlChangeDiff";
import { isControlEntity } from "../../../lib/controlDiffUtils";

interface ApprovalDetailDialogProps {
  requestId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onActionComplete: () => void;
}

function SnapshotDiff({
  current,
  proposed,
}: {
  current: Record<string, unknown> | null | undefined;
  proposed: Record<string, unknown> | null | undefined;
}) {
  if (!current && !proposed) return null;

  const allKeys = Array.from(
    new Set([...Object.keys(current ?? {}), ...Object.keys(proposed ?? {})]),
  );

  if (allKeys.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Proposed Changes</h3>
      <div className="border border-stone-200 rounded-md overflow-hidden">
        {allKeys.map((key) => {
          const currentVal = current?.[key];
          const proposedVal = proposed?.[key];
          const changed =
            JSON.stringify(currentVal) !== JSON.stringify(proposedVal);

          return (
            <div
              key={key}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm border-b border-stone-100 last:border-b-0 ${changed ? "bg-amber-50/50" : ""}`}
            >
              <span className="font-medium text-stone-500 w-36 flex-shrink-0">
                {formatSnapshotFieldName(key)}
              </span>
              {changed ? (
                <div className="flex items-baseline gap-2 min-w-0 flex-wrap">
                  <span className="text-stone-400 line-through break-words">
                    {formatSnapshotValue(currentVal)}
                  </span>
                  <ArrowRight className="h-3 w-3 text-stone-400 flex-shrink-0" />
                  <span className="font-medium break-words">
                    {formatSnapshotValue(proposedVal)}
                  </span>
                </div>
              ) : (
                <span className="text-stone-600 break-words">
                  {formatSnapshotValue(currentVal)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const ApprovalDetailDialog = ({
  requestId,
  open,
  onOpenChange,
  onActionComplete,
}: ApprovalDetailDialogProps) => {
  const { workspaceMember } = useAuth();
  const { fetchRequest, approveRequest, rejectRequest, cancelRequest } =
    useApprovalRequests({ fetchOnLoad: false });

  const [request, setRequest] = useState<ApprovalRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const loadRequest = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const data = await fetchRequest(requestId);
      if (data) {
        setRequest(data);
      }
    } finally {
      setLoading(false);
    }
  }, [requestId, fetchRequest]);

  useEffect(() => {
    if (open && requestId) {
      loadRequest();
      setComment("");
    }
  }, [open, requestId, loadRequest]);

  const handleAction = useCallback(
    async (action: "approve" | "reject" | "cancel") => {
      if (!requestId) return;
      setActionLoading(true);
      try {
        if (action === "approve") {
          await approveRequest(requestId, comment || undefined);
        } else if (action === "reject") {
          await rejectRequest(requestId, comment || undefined);
        } else {
          await cancelRequest(requestId, comment || undefined);
        }
        onOpenChange(false);
        onActionComplete();
      } finally {
        setActionLoading(false);
      }
    },
    [
      requestId,
      comment,
      approveRequest,
      rejectRequest,
      cancelRequest,
      onOpenChange,
      onActionComplete,
    ],
  );

  const isAdmin = hasPermission(workspaceMember, "manage_approvals");
  const isPending = request?.status === "pending";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[80vw] max-w-[80vw] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Approval Request</DialogTitle>
          <DialogDescription>
            Review the details and take action on this request.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : request ? (
            <div className="space-y-6 pb-2">
              {/* Request Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      request.status === "pending"
                        ? "secondary"
                        : request.status === "approved"
                          ? "default"
                          : "destructive"
                    }
                  >
                    {formatStatus(request.status)}
                  </Badge>
                </div>
                {request.description && (
                  <p className="text-sm text-stone-600">
                    {request.description}
                  </p>
                )}
                {getEntityLink(request.entityType, request.entityId) && (
                  <Link
                    to={
                      getEntityLink(request.entityType, request.entityId) ?? ""
                    }
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View {formatEntityType(request.entityType)}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-stone-500">Type</span>
                    <p>{formatEntityType(request.entityType)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-stone-500">Change</span>
                    <p>{formatChangeType(request.changeType)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-stone-500">
                      Requester
                    </span>
                    <p>
                      {request.requester?.email ??
                        request.requesterEmail ??
                        "Deleted user"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-stone-500">Created</span>
                    <p>{formattedDate(request.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Snapshot Diff */}
              {request.changeDiff && isControlEntity(request.entityType) ? (
                <ControlChangeDiff changeDiff={request.changeDiff} />
              ) : (
                <SnapshotDiff
                  current={request.currentSnapshot}
                  proposed={request.proposedSnapshot}
                />
              )}

              {/* Action History */}
              {request.actions && request.actions.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">History</h3>
                  <div className="space-y-2">
                    {request.actions.map((action) => (
                      <div
                        key={action.id}
                        className="flex items-start gap-3 text-sm border-l-2 border-stone-200 pl-3 py-1"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {formatStatus(action.action)}
                            </Badge>
                            <span className="text-xs text-stone-400">
                              {formattedDate(action.createdAt)}
                            </span>
                          </div>
                          {action.comment && (
                            <p className="text-stone-600 mt-1">
                              {action.comment}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comment and Actions */}
              {isPending && isAdmin && (
                <div className="space-y-3">
                  <Textarea
                    placeholder="Add a comment (optional)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="resize-none"
                    rows={3}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-stone-500 py-4">Request not found.</p>
          )}
        </div>

        {request && isPending && isAdmin && (
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => handleAction("cancel")}
              disabled={actionLoading}
            >
              Cancel Request
            </Button>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => handleAction("reject")}
              disabled={actionLoading}
            >
              Reject
            </Button>
            <Button
              onClick={() => handleAction("approve")}
              disabled={actionLoading}
            >
              Approve
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
